import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { getApiErrorMessage } from '@noor/api';
import { toast } from '@noor/lib';
import {
  conversationsApi,
  conversationsKeys,
  type ConversationId,
} from '@/entities/conversation';

/** `POST chat/{id}/notes` — pin an operator-only note to the thread. */
export function useCreateChatNote(chatId: ConversationId) {
  const { t } = useTranslation('inbox');
  const queryClient = useQueryClient();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (text: string) => conversationsApi.createNote(chatId, text),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: conversationsKeys.notes(chatId) }),
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.noteCreate'))),
  });
}

/** `DELETE chat/{id}/notes/{noteId}`. */
export function useDeleteChatNote(chatId: ConversationId) {
  const { t } = useTranslation('inbox');
  const queryClient = useQueryClient();
  return useMutation({
    meta: { suppressErrorToast: true },
    mutationFn: (noteId: ConversationId) => conversationsApi.deleteNote(chatId, noteId),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: conversationsKeys.notes(chatId) }),
    onError: (error) => toast.error(getApiErrorMessage(error, t('errors.noteDelete'))),
  });
}
